// ModuleDataEdit.jsx  – “silince kasma” fix’i
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Editor } from "@tinymce/tinymce-react";
import {
	SingleImageDropzone,
	MultipleImageDropzone,
} from "../DropzoneComponents";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FileDropzone from "../FileDropzone";

function ensureParents(ids, list) {
	const out = new Set(ids);
	const parents = Object.fromEntries(list.map((c) => [c.id, c.parent]));
	ids.forEach((id) => {
		let p = parents[id];
		while (p) {
			out.add(p);
			p = parents[p];
		}
	});
	return [...out];
}

function sameSet(a, b) {
	if (a.length !== b.length) return false;
	const s = new Set(a);
	return b.every((x) => s.has(x));
}

function renderField(el, values, setValues) {
	const onChange = (e) =>
		setValues((p) => ({ ...p, [el.name]: e.target.value }));
	const CDN_PREFIX = process.env.REACT_APP_CDN_URL || "";

	switch (el.type) {
		case "text":
		case "shortDate":
		case "longDate":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<input
						type={
							el.type === "shortDate"
								? "date"
								: el.type === "longDate"
									? "datetime-local"
									: "text"
						}
						className="form-control"
						placeholder={el.placeholder || ""}
						value={values[el.name] || ""}
						onChange={onChange}
						required={!!el.required}
					/>
				</div>
			);
		case "textarea":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<textarea
						className="form-control"
						rows={3}
						placeholder={el.placeholder || ""}
						value={values[el.name] || ""}
						onChange={onChange}
						required={!!el.required}
					/>
				</div>
			);
	case "editor":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<Editor
						tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
						value={values[el.name] || ""}
						onEditorChange={(content) =>
							setValues((p) => ({ ...p, [el.name]: content }))
						}
						init={{
							promotion: false,
							branding: false,
							height: 400,
							plugins: [
								"advlist",
								"autolink",
								"link",
								"image",
								"lists",
								"charmap",
								"preview",
								"anchor",
								"pagebreak",
								"searchreplace",
								"wordcount",
								"visualblocks",
								"code",
								"fullscreen",
								"insertdatetime",
								"media",
								"table",
								"emoticons",
								"help",
							],
							toolbar:
								"undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
								"bullist numlist outdent indent | link fileupload image | " +
								"print preview media fullscreen code | forecolor backcolor emoticons | help",
							content_style:
								"body{font-family:Helvetica,Arial,sans-serif;font-size:16px} " +
								"a{color:#0d6efd;text-decoration:underline;cursor:pointer}",
							image_advtab: true,

							setup: (editor) => {
								editor.ui.registry.addButton("fileupload", {
									text: "Dosya Ekle",
									tooltip: "Seçili yazıya dosya bağla veya mevcut dosyayı değiştir",

									onAction: () => {
										/*
										 * Dosya seçme penceresi açılmadan önce hem seçimi
										 * hem de imlecin içinde bulunduğu mevcut linki sakla.
										 */
										const bookmark = editor.selection.getBookmark(2, true);
										const hasSelectedText = !editor.selection.isCollapsed();
										const selectedNode = editor.selection.getNode();
										const selectedLink = editor.dom.getParent(selectedNode, "a");

										const input = document.createElement("input");
										input.type = "file";
										input.accept =
											".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv";

										input.onchange = async () => {
											const file = input.files?.[0];
											if (!file) return;

											try {
												const token = localStorage.getItem("admin_token");
												const formData = new FormData();
												formData.append("file", file);

												const response = await fetch(
													`${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
													{
														method: "POST",
														headers: {
															Authorization: `Bearer ${token}`,
														},
														body: formData,
													},
												);

												if (!response.ok) {
													throw new Error(`Dosya yüklenemedi: ${response.status}`);
												}

												const data = await response.json();
												if (!data.location) {
													throw new Error("Sunucudan dosya bağlantısı dönmedi.");
												}

												/*
												 * İmleç mevcut bir linkin içindeyse link yazısını
												 * koru ve sadece indirilecek dosyanın adresini değiştir.
												 */
												if (
													selectedLink &&
													editor.getBody().contains(selectedLink)
												) {
													editor.dom.setAttribs(selectedLink, {
														href: data.location,
														target: "_blank",
														rel: "noopener noreferrer",
														download: file.name,
													});
													editor.selection.select(selectedLink);
													editor.nodeChanged();
												} else {
													editor.focus();
													editor.selection.moveToBookmark(bookmark);

													if (hasSelectedText) {
														editor.execCommand("mceInsertLink", false, {
															href: data.location,
															target: "_blank",
															rel: "noopener noreferrer",
														});

														const createdLink = editor.dom.getParent(
															editor.selection.getNode(),
															"a",
														);

														if (createdLink) {
															editor.dom.setAttrib(createdLink, "download", file.name);
														}
													} else {
														const safeFileName = editor.dom.encode(file.name);
														const safeLocation = editor.dom.encode(data.location);

														editor.insertContent(
															`<a href="${safeLocation}" target="_blank" rel="noopener noreferrer" download="${safeFileName}">${safeFileName}</a>`,
														);
													}
												}

												toast.success(
													selectedLink
														? "Dosya bağlantısı güncellendi."
														: "Dosya bağlantısı eklendi.",
												);
											} catch (error) {
												console.error("TinyMCE dosya yükleme hatası:", error);
												toast.error(error.message || "Dosya yüklenemedi.");
											}
										};

										input.click();
									},
								});
							},

							automatic_uploads: true,
							file_picker_types: "image file",
							block_unsupported_drop: false,
							paste_data_images: false,
							images_upload_handler: (blobInfo) => {
								return new Promise((resolve, reject) => {
									const token = localStorage.getItem("admin_token");
									const formData = new FormData();
									formData.append("file", blobInfo.blob(), blobInfo.filename());

									fetch(
										`${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
										{
											method: "POST",
											headers: { Authorization: `Bearer ${token}` },
											body: formData,
										},
									)
										.then((res) => {
											if (!res.ok)
												throw new Error("Upload failed: " + res.status);
											return res.json();
										})
										.then((data) => {
											if (!data.location) throw new Error("location alanı yok");
											resolve(data.location);
										})
										.catch((err) => reject(err.message));
								});
							},
						}}
					/>
				</div>
			);
		case "image":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">
						{el.label} ({el.maxWidth}×{el.maxHeight})
					</label>

					<SingleImageDropzone
						fieldName={el.name}
						fieldValue={values[el.name]}
						setFieldValues={setValues}
						required={!!el.required}
						maxWidth={el.maxWidth}
						maxHeight={el.maxHeight}
					/>
				</div>
			);
		case "images":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">
						{el.label} ({el.maxWidth}×{el.maxHeight})
					</label>
					<MultipleImageDropzone
						fieldName={el.name}
						fieldValue={values[el.name]}
						setFieldValues={setValues}
						required={!!el.required}
						maxWidth={el.maxWidth}
						maxHeight={el.maxHeight}
					/>
				</div>
			);
		case "file":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>

					<FileDropzone
						value={values[el.name] || ""}
						onChange={(file) =>
							setValues((prev) => ({
								...prev,
								[el.name]: file,
							}))
						}
						required={!!el.required}
						accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv"
					/>
				</div>
			);
		case "files":
			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>

					<FileDropzone
						multiple
						value={
							Array.isArray(values[el.name])
								? values[el.name]
								: values[el.name]
									? [values[el.name]]
									: []
						}
						onChange={(files) =>
							setValues((prev) => ({
								...prev,
								[el.name]: files,
							}))
						}
						required={!!el.required}
						accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.txt,.csv"
					/>
				</div>
			);
		case "select": {
			const titles = (el.content_titles || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const valuesArr = (el.content_values || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const opts = valuesArr.map((v, i) => ({
				value: v,
				label: titles[i] || v,
			}));

			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<Select
						options={opts}
						value={opts.find((o) => o.value === values[el.name]) || null}
						isClearable={!el.required}
						placeholder={el.placeholder || "Seçin"}
						onChange={(o) =>
							setValues((prev) => ({
								...prev,
								[el.name]: o ? o.value : "",
							}))
						}
					/>
				</div>
			);
		}

		case "multiselect": {
			const titles = (el.content_titles || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const valuesArr = (el.content_values || "")
				.split(",")
				.map((s) => s.trim())
				.filter(Boolean);

			const opts = valuesArr.map((v, i) => ({
				value: v,
				label: titles[i] || v,
			}));

			return (
				<div className="mb-3" key={el.name}>
					<label className="form-label fw-bold">{el.label}</label>
					<CreatableSelect
						isMulti
						options={opts}
						value={
							Array.isArray(values[el.name])
								? opts.filter((o) => values[el.name].includes(o.value))
								: []
						}
						placeholder={el.placeholder || "Seçim yapın"}
						onChange={(arr) =>
							setValues((prev) => ({
								...prev,
								[el.name]: arr.map((o) => o.value),
							}))
						}
					/>
				</div>
			);
		}
		default:
			return null;
	}
}

export default function ModuleDataEdit() {
	const { slug, id } = useParams();
	const navigate = useNavigate();

	const [moduleInfo, setModuleInfo] = useState(null);
	const [fieldValues, setFieldValues] = useState({});
	const [loading, setLoading] = useState(false);
	const [recordLang, setRecordLang] = useState("tr");
	const [seoData, setSeoData] = useState({
		seo_title: "",
		seo_description: "",
	});
	const [allCats, setAllCats] = useState([]);
	const [options, setOptions] = useState([]);
	const [selected, setSelected] = useState([]);
	const [selectedCats, setSelectedCats] = useState([]);

	useEffect(() => {
		(async () => {
			const token = localStorage.getItem("admin_token");

			const mod = await axios.get(
				`${process.env.REACT_APP_API_BASE_URL}/api/modules/by-slug/${slug}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			setModuleInfo(mod.data);

			const rec = await axios.get(
				`${process.env.REACT_APP_API_BASE_URL}/api/module-data/${id}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			const r = rec.data;
			setFieldValues(JSON.parse(r.data || "{}"));
			setSeoData({
				seo_title: r.seo_title || "",
				seo_description: r.seo_description || "",
			});
			if (r.lang) setRecordLang(r.lang);
			if (r.category?.trim()) {
				setSelectedCats(
					r.category
						.split(",")
						.map((s) => +s.trim())
						.filter(Boolean),
				);
			}
		})().catch(console.error);
	}, [slug, id]);

	useEffect(() => {
		if (!moduleInfo) return;
		// sadece "category" aktifse fetch et
		if (Number(moduleInfo.category) !== 1) return;

		const token = localStorage.getItem("admin_token");
		axios
			.get(
				`${process.env.REACT_APP_API_BASE_URL}/api/category?module_id=${moduleInfo.id}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			)
			.then(({ data }) => {
				// data: [{ id, title, parent, … }, …]
				setAllCats(data);
				setOptions(
					data.map((c) => ({
						value: c.id,
						label: c.name, // API’den gelen başlık alanına göre uyarlayın
					})),
				);
			})
			.catch((err) => console.error("Kategori listesi alınamadı:", err));
	}, [moduleInfo]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		console.log("🚀 SUBMIT TETIKLENDI", e.target);
		console.log("🔔 handleSubmit tetiklendi", { slug, id, moduleInfo });

		if (!moduleInfo) {
			console.warn("⚠️ moduleInfo henüz yüklenmedi, çıkılıyor");
			return;
		}

		setLoading(true);
		const token = localStorage.getItem("admin_token");
		console.log("🎫 token:", token);

		const fd = new FormData();
		fd.append("module_id", moduleInfo.id);
		fd.append("slug", slug);
		const lang =
			new URLSearchParams(window.location.search).get("lang") || recordLang;
		fd.append("lang", lang);
		fd.append("org_id", "0");
		fd.append("category", selectedCats.join(","));
		fd.append("seo_title", seoData.seo_title.trim());
		fd.append("seo_description", seoData.seo_description.trim());

		const dataCopy = { ...fieldValues };
		console.log("🔸 Orijinal fieldValues:", fieldValues);

		Object.entries(dataCopy).forEach(([k, v]) => {
			// Tekli image/file alanı
			if (v instanceof File) {
				fd.append(k, v);
				delete dataCopy[k];
				return;
			}

			// Çoklu image/file alanı
			if (Array.isArray(v)) {
				const existingValues = [];
				const newFiles = [];

				v.forEach((item) => {
					// Eski kayıtlı resim yolu
					if (typeof item === "string" && item.trim() !== "") {
						existingValues.push(item);
						return;
					}

					// Yeni eklenen veya düzenlenen File
					if (item instanceof File) {
						newFiles.push(item);
						return;
					}

					// Bozuk {}, null, undefined kayıtları alma
				});

				// JSON içinde sadece eski string yollar kalsın
				dataCopy[k] = existingValues;

				// Yeni dosyaları FormData'ya ekle
				newFiles.forEach((file) => {
					fd.append(`${k}[]`, file);
				});
			}
		});

		fd.append("data", JSON.stringify(dataCopy));
		console.log("🔸 Gönderilecek JSON kısmı:", dataCopy);

		console.log("📦 FormData içeriği:");
		fd.forEach((value, key) => {
			console.log(" ", key, value);
		});

		try {
			console.log("🚀 PUT isteği yollanıyor...");
			const response = await axios.put(
				`${process.env.REACT_APP_API_BASE_URL}/api/module-data/${id}`,
				fd,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);
			console.log(" PUT yanıtı:", response.status, response.data);
			toast.success("Kayıt başarıyla göncellendi!");
			setTimeout(() => {
				navigate(`/modules/${slug}`);
			}, 1000);
		} catch (err) {
			console.error("❌ PUT hatası:", err);
			if (err.response) {
				console.error(">>> response.status:", err.response.status);
				console.error(">>> response.data:", err.response.data);
			} else if (err.request) {
				console.error(">>> request (yanıtsız):", err.request);
			}
			alert("Kayıt güncellenemedi!");
		} finally {
			setLoading(false);
		}
	};

	if (!moduleInfo) return <p>Yükleniyor…</p>;
	const elements = moduleInfo.elements ? JSON.parse(moduleInfo.elements) : [];

return (
  <div className="container my-4">
    <ToastContainer />

    <form onSubmit={handleSubmit}>
      {/* ================= İÇERİK KARTI ================= */}
      <div className="card shadow-sm p-4 mb-4">
        
        {/* ===== KATEGORİ SEÇİMİ ===== */}
        {+moduleInfo.category === 1 && (
          <div className="mb-4">
            <label className="form-label fw-semibold">
              Kategoriler
            </label>

            {+moduleInfo.multiple_category === 1 ? (
              <Select
                isMulti
                options={options}
                closeMenuOnSelect={false}
                placeholder="Kategori seçin"
                value={options.filter((option) =>
                  selectedCats.includes(option.value),
                )}
                onChange={(selectedOptions) => {
                  const ids = selectedOptions.map(
                    (option) => option.value,
                  );

                  setSelectedCats(
                    ensureParents(ids, allCats),
                  );
                }}
              />
            ) : (
              <Select
                options={options}
                isClearable
                placeholder="Kategori seçin"
                value={
                  options.find(
                    (option) =>
                      option.value === selectedCats[0],
                  ) || null
                }
                onChange={(option) => {
                  const ids = option
                    ? ensureParents(
                        [option.value],
                        allCats,
                      )
                    : [];

                  setSelectedCats(ids);
                }}
              />
            )}
          </div>
        )}

        {elements.map((element) =>
          renderField(
            element,
            fieldValues,
            setFieldValues,
          ),
        )}
      </div>

      {/* ================= SEO KARTI ================= */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0 fw-bold">
            SEO Ayarları
          </h5>
        </div>

        <div className="card-body p-4">
          <div className="mb-3">
            <label
              htmlFor="seo_title"
              className="form-label fw-bold"
            >
              SEO Başlık
            </label>

            <input
              type="text"
              id="seo_title"
              name="seo_title"
              className="form-control"
              maxLength={255}
              value={seoData.seo_title}
              placeholder="Arama motorlarında görünecek başlık"
              onChange={(event) =>
                setSeoData((previous) => ({
                  ...previous,
                  seo_title: event.target.value,
                }))
              }
            />

            <div className="form-text text-end">
              {seoData.seo_title.length}/255
            </div>
          </div>

          <div className="mb-0">
            <label
              htmlFor="seo_description"
              className="form-label fw-bold"
            >
              SEO Açıklama
            </label>

            <textarea
              id="seo_description"
              name="seo_description"
              className="form-control"
              rows={4}
              maxLength={500}
              value={seoData.seo_description}
              placeholder="Arama motorlarında görünecek sayfa açıklaması"
              onChange={(event) =>
                setSeoData((previous) => ({
                  ...previous,
                  seo_description:
                    event.target.value,
                }))
              }
            />

            <div className="form-text text-end">
              {seoData.seo_description.length}/500
            </div>
          </div>
        </div>
      </div>

      {/* ================= KAYDET ================= */}
      <div className="d-flex justify-content-center">
        <button
          type="submit"
          className="btn btn-primary px-5"
          disabled={loading}
        >
          {loading
            ? "Kaydediliyor…"
            : "Kaydet"}
        </button>
      </div>
    </form>
  </div>
);
}
